import _ from 'lodash';

import api, { Base, Tags } from 'ev-api/api';
import {
  sanitizeAndTransformResponse,
  sanitizeAndTransformResponseDataAttributes,
} from 'ev-api/common/transformers';

import {
  CreateEDataParams,
  EDataCategoriesResponse,
  EDataDefinition,
  EDataDefinitionCreationResponse,
  EDataDefinitionResponse,
  EDataDefinitionUpdateResponse,
  EDataDefinitionUsageItem,
  EDataDefinitionUsageParams,
  EDataFilterParams,
  OwnerResponse,
  UpdateEDataParams,
} from './responses';

const edataApi = api.injectEndpoints({
  endpoints: builder => ({
    getEdataDefinitions: builder.query<EDataDefinition[], EDataFilterParams>({
      query: filters => {
        const params = new URLSearchParams();

        if (filters.label_cont) {
          params.append('filters[label_cont]', filters.label_cont);
        }

        if (filters.category_ids) {
          const categoryIdsArray = Array.isArray(filters.category_ids)
            ? filters.category_ids
            : [filters.category_ids];

          categoryIdsArray.forEach(id => {
            params.append('category_ids[]', id.toString());
          });
        }

        if (filters.created_by_user_ids) {
          const createdByIdsArray = Array.isArray(filters.created_by_user_ids)
            ? filters.created_by_user_ids
            : [filters.created_by_user_ids];

          createdByIdsArray.forEach(id => {
            params.append('created_by_user_ids[]', id.toString());
          });
        }

        if (filters.field_types) {
          const fieldTypesArray = Array.isArray(filters.field_types)
            ? filters.field_types
            : [filters.field_types];

          fieldTypesArray.forEach(id => {
            params.append('field_types[]', id.toString());
          });
        }

        if (filters.sort_by) {
          params.append('sort_by', filters.sort_by);
        }

        if (filters.sort_direction) {
          params.append('sort_direction', filters.sort_direction);
        }

        return {
          url: `${Base.V3}/edata/definitions?${params.toString()}`,
        };
      },
      transformResponse: (resp: EDataDefinitionResponse) => {
        const cloneData = _.cloneDeep(resp);
        cloneData.data.map(d => {
          let ds;

          if (d.attributes.dataset) {
            ds = JSON.parse(d.attributes.dataset as string);
            d.attributes.dataset = ds;
          }

          return d;
        });
        return sanitizeAndTransformResponse(cloneData);
      },
      providesTags: [Tags.EData],
    }),
    getEdataDefinitionCategories: builder.query<
      Array<{ id: string; name: string }>,
      void
    >({
      query: () => `${Base.V3}/edata/definition_categories`,
      transformResponse: (resp: EDataCategoriesResponse) =>
        resp.data.map(category => ({
          id: String(category.id),
          name: category.attributes.name,
        })),
      providesTags: [Tags.EData],
    }),

    getEdataDefinitionOwners: builder.query<
      Array<{
        id: string;
        firstName: string;
        lastName: string;
        avatar: string;
      }>,
      void
    >({
      query: () => `${Base.V3}/edata/definitions/field_owners`,
      transformResponse: (resp: { data: OwnerResponse[] }) =>
        resp.data.map(owner => ({
          id: String(owner.id),
          firstName: owner.attributes.first_name,
          lastName: owner.attributes.last_name,
          avatar: owner.attributes.avatar,
        })),
      providesTags: [Tags.EData],
    }),

    getEdataDefinitionUsage: builder.query<
      EDataDefinitionUsageItem[],
      EDataDefinitionUsageParams
    >({
      query: ({ eDataDefinitionId }) =>
        `${Base.V3}/edata/definitions/${eDataDefinitionId}/usage`,
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.EData],
      keepUnusedDataFor: 0,
    }),

    createEdataDefinition: builder.mutation<
      EDataDefinitionCreationResponse,
      CreateEDataParams
    >({
      query: definition => {
        const { children, definition_texts, ...restDefinition } = definition;

        return {
          url: `${Base.V3}/edata/definitions`,
          method: 'POST',
          body: {
            edata_definition: {
              ...restDefinition,
              children_attributes: children,
              definition_texts_attributes: definition_texts,
            },
          },
        };
      },
      transformResponse: sanitizeAndTransformResponseDataAttributes,
      invalidatesTags: [Tags.EData],
    }),

    updateEdataDefinition: builder.mutation<
      EDataDefinitionUpdateResponse,
      UpdateEDataParams
    >({
      query: definition => {
        const { id, children, definition_texts, ...restDefinition } =
          definition;

        return {
          url: `${Base.V3}/edata/definitions/${id}`,
          method: 'PATCH',
          body: {
            edata_definition: {
              ...restDefinition,
              children_attributes: children,
              definition_texts_attributes: definition_texts,
            },
          },
        };
      },
      transformResponse: sanitizeAndTransformResponseDataAttributes,
      invalidatesTags: [Tags.EData],
    }),
  }),
});

export const {
  useGetEdataDefinitionsQuery,
  useCreateEdataDefinitionMutation,
  useUpdateEdataDefinitionMutation,
  useGetEdataDefinitionCategoriesQuery,
  useGetEdataDefinitionOwnersQuery,
  useGetEdataDefinitionUsageQuery,
} = edataApi;
